import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { BRAND_ICON, HEADER_LOGO } from "../../config/brand";

/* Brand logo: official Logo V1 badge lockup (used site-wide) */
const LOGO_SIZES = {
  sm: "h-10",
  default: "h-12 sm:h-14",
  lg: "h-[4.25rem] sm:h-[5.5rem]",
  xl: "h-[5rem] sm:h-[6.5rem]",
};
export function Logo({ to = "/", className, size = "default", blend = false }) {
  return (
    <Link to={to} data-testid="brand-logo" className={cn("inline-flex items-center group", className)}>
      <img
        src={BRAND_ICON}
        alt="Clacton Fireworks"
        className={cn(
          "w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]",
          blend
            ? "mix-blend-screen [filter:drop-shadow(0_0_14px_rgba(232,181,75,0.35))]"
            : "drop-shadow-[0_0_20px_rgba(232,181,75,0.22)]",
          LOGO_SIZES[size] || LOGO_SIZES.default
        )}
      />
    </Link>
  );
}

/* Website header brand: renders the dedicated horizontal header logo when one
   is supplied; until then, a small low-emphasis circular emblem. The wrapper
   reserves a fixed horizontal slot so the future horizontal logo drops in
   cleanly without shifting the navigation. */
export function HeaderBrand({ className }) {
  return (
    <Link
      to="/"
      data-testid="brand-logo"
      aria-label="Clacton Fireworks — home"
      className={cn("inline-flex items-center shrink-0 min-w-[150px] sm:min-w-[220px]", className)}
    >
      {HEADER_LOGO ? (
        <img
          src={HEADER_LOGO}
          alt="Clacton Fireworks"
          className="h-12 md:h-14 lg:h-[68px] w-auto object-contain object-left transition-transform duration-300 hover:scale-[1.02]"
        />
      ) : (
        <img
          src={BRAND_ICON}
          alt="Clacton Fireworks"
          className="h-20 md:h-[100px] lg:h-[120px] w-auto object-contain object-left drop-shadow-[0_0_18px_rgba(232,181,75,0.28)] transition-transform duration-300 hover:scale-[1.03]"
        />
      )}
    </Link>
  );
}

export function GlassCard({ className, hover = true, children, ...props }) {
  return (
    <div className={cn("cf-glass rounded-2xl", hover && "cf-glass-hover", className)} {...props}>
      {children}
    </div>
  );
}

export function SectionTitle({ eyebrow, title, subtitle, className, align = "left", as: As = "h2" }) {
  return (
    <div className={cn(align === "center" && "text-center mx-auto", "max-w-3xl", className)}>
      {eyebrow && (
        <p className="text-xs tracking-[0.3em] uppercase font-bold cf-gold-text mb-3">{eyebrow}</p>
      )}
      <As className="font-playfair text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-white">
        {title}
      </As>
      {subtitle && <p className="mt-3 text-muted-foreground text-base leading-relaxed">{subtitle}</p>}
    </div>
  );
}

const STATUS_STYLES = {
  Received: "bg-white/10 text-white/80",
  "Under Review": "bg-[#F5A623]/15 text-amber",
  Confirmed: "bg-[#E8B54B]/15 text-gold",
  "Ready for Collection": "bg-[#F2761B]/15 text-fireorange",
  Collected: "bg-emerald-500/15 text-emerald-400",
  Cancelled: "bg-[#D23B2E]/15 text-firered",
};

export function StatusBadge({ status, className }) {
  return (
    <span
      data-testid={`status-badge-${String(status).toLowerCase().replace(/\s+/g, "-")}`}
      className={cn(
        "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold tracking-wide",
        STATUS_STYLES[status] || "bg-white/10 text-white/70",
        className
      )}
    >
      {status}
    </span>
  );
}

export function RoleBadge({ role, className }) {
  const label = { super_admin: "Super Admin", manager: "Manager", staff: "Staff", customer: "Customer" }[role] || role;
  return (
    <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold bg-gold/15 text-gold border border-gold/20", className)}>
      {label}
    </span>
  );
}

export function PageLoader({ label = "Loading" }) {
  return (
    <div className="min-h-screen cf-bg grid place-items-center" data-testid="page-loader">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
        <span className="text-sm tracking-wide">{label}…</span>
      </div>
    </div>
  );
}
